import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getRoles, getUserEffectivePermissions } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Users, Plus, Pencil, Trash2, Loader2, RefreshCw, Key, Lock, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { toast } from 'sonner';

const ALL_PERMISSIONS = [
  'bonieren','offene_bestellungen','storno','zahlen','tisch_wechsel','preis_anpassen',
  'rabatt_geben','gang_abschicken','bestellung_drucken',
  'zahlung_bar','zahlung_karte','zahlung_gutschein','zahlung_intern','zahlung_ueberweisung',
  'berichte_sehen','berichte_exportieren','kassenabschluss_sehen','kassenabschluss_durchfuehren',
  'benutzer_verwalten','artikel_verwalten','kunden_verwalten','gutscheine_verwalten',
  'lager_verwalten','kassenbuch_verwalten','einstellungen_verwalten','tische_verwalten',
  'kassa_oeffnen','kassa_schliessen','nullbeleg_erstellen','jahresbeleg_erstellen',
];

const emptyUser = {
  username: '', password: '', pin: '', name: '', role: 'waiter',
  permissions: [], roles: [], locked_permissions: [], allowed_registers: [],
};

function RoleChip({ role, onRemove }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: role.color || '#1a2f38' }}
    >
      {role.name}
      {onRemove && (
        <button onClick={() => onRemove(role.id)} className="hover:opacity-70 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [rolesError, setRolesError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyUser);
  const [showLockedSection, setShowLockedSection] = useState(false);
  const [showRegistersSection, setShowRegistersSection] = useState(false);
  const [effectivePerms, setEffectivePerms] = useState(null);
  const [loadingEffective, setLoadingEffective] = useState(false);
  const [newRegister, setNewRegister] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setIsLoading(true);
    setLoadError('');
    setRolesError(false);
    try {
      const [usersResult, rolesResult] = await Promise.allSettled([getUsers(), getRoles()]);
      if (rolesResult.status === 'fulfilled') setRoles(rolesResult.value);
      else setRolesError(true);
      if (usersResult.status === 'fulfilled') setUsers(usersResult.value);
      else {
        const status = usersResult.reason?.response?.status;
        setLoadError(status >= 500
          ? `Die Benutzer konnten wegen eines Serverfehlers (${status}) nicht geladen werden. Bitte erneut versuchen.`
          : status ? 'Die Benutzer konnten nicht geladen werden. Bitte erneut versuchen.'
            : 'Der Server ist nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        pin: user.pin || '',
        name: user.name,
        role: user.role,
        permissions: user.permissions || [],
        roles: user.roles || [],
        locked_permissions: user.locked_permissions || [],
        allowed_registers: user.allowed_registers || [],
      });
    } else {
      setEditingUser(null);
      setFormData(emptyUser);
    }
    setShowLockedSection(false);
    setShowRegistersSection(false);
    setEffectivePerms(null);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.username.trim()) {
      toast.error('Bitte Name und Benutzername ausfüllen');
      return;
    }
    if (!editingUser && !formData.password && formData.role !== 'waiter') {
      toast.error('Passwort erforderlich für Admin/Manager');
      return;
    }
    if (formData.role === 'waiter' && !formData.pin) {
      toast.error('PIN erforderlich für Kellner');
      return;
    }

    try {
      if (editingUser) {
        await updateUser(editingUser.id, {
          name: formData.name,
          role: formData.role,
          pin: formData.pin,
          permissions: formData.permissions,
          roles: formData.roles,
          locked_permissions: formData.locked_permissions,
          allowed_registers: formData.allowed_registers,
        });
        toast.success('Benutzer aktualisiert');
      } else {
        await createUser(formData);
        toast.success('Benutzer erstellt');
      }
      setShowDialog(false);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Benutzer wirklich deaktivieren?')) return;
    try {
      await deleteUser(userId);
      toast.success('Benutzer deaktiviert');
      loadAll();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleRole = (roleId) => {
    setFormData(f => ({
      ...f,
      roles: f.roles.includes(roleId)
        ? f.roles.filter(r => r !== roleId)
        : [...f.roles, roleId],
    }));
    setEffectivePerms(null);
  };

  const toggleLockedPerm = (perm) => {
    setFormData(f => ({
      ...f,
      locked_permissions: f.locked_permissions.includes(perm)
        ? f.locked_permissions.filter(p => p !== perm)
        : [...f.locked_permissions, perm],
    }));
    setEffectivePerms(null);
  };

  const addRegister = () => {
    const v = newRegister.trim();
    if (!v || formData.allowed_registers.includes(v)) return;
    setFormData(f => ({ ...f, allowed_registers: [...f.allowed_registers, v] }));
    setNewRegister('');
  };

  const loadEffective = async () => {
    if (!editingUser) return;
    setLoadingEffective(true);
    try {
      const res = await getUserEffectivePermissions(editingUser.id);
      setEffectivePerms(res.effective_permissions || []);
    } catch {
      toast.error('Fehler beim Berechnen');
    } finally {
      setLoadingEffective(false);
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin:   { label: 'Admin',   className: 'bg-red-100 text-red-800' },
      manager: { label: 'Manager', className: 'bg-blue-100 text-blue-800' },
      waiter:  { label: 'Kellner', className: 'bg-green-100 text-green-800' },
    };
    const cfg = variants[role] || variants.waiter;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Benutzer & Rollen</h1>
          <p className="text-slate-500">{loadError ? 'Benutzerliste derzeit nicht verfügbar' : `${users.filter(u => u.is_active !== false).length} aktive Benutzer`}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadAll}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button disabled={!!loadError || rolesError} className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => handleOpenDialog()} data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Benutzer
          </Button>
        </div>
      </div>

      {loadError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>}
      {rolesError && <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Rollen konnten nicht geladen werden. Die Benutzerliste bleibt lesbar; Bearbeiten ist erst nach erfolgreichem Laden der Rollen möglich.</div>}

      <Card className="shadow-sm" hidden={!!loadError}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Name</TableHead>
                <TableHead>Benutzername</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Rollen-Gruppen</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const userRoleObjs = roles.filter(r => (user.roles || []).includes(r.id));
                return (
                  <TableRow key={user.id} className={user.is_active === false ? 'opacity-50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1a2f38] flex items-center justify-center">
                          <span className="text-white font-bold">{user.name?.charAt(0) || '?'}</span>
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{user.username}</TableCell>
                    <TableCell>
                      {user.pin ? <span className="font-mono bg-slate-100 px-2 py-1 rounded">{user.pin}</span> : '—'}
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {userRoleObjs.length > 0
                          ? userRoleObjs.map(r => <RoleChip key={r.id} role={r} />)
                          : <span className="text-xs text-slate-400">—</span>
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                        {user.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button disabled={rolesError} variant="ghost" size="icon" onClick={() => handleOpenDialog(user)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(user.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Basic fields */}
            <div>
              <Label>Name *</Label>
              <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Max Mustermann" data-testid="user-name-input" />
            </div>
            <div>
              <Label>Benutzername *</Label>
              <Input value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} placeholder="max.mustermann" disabled={!!editingUser} />
            </div>
            <div>
              <Label>Systemrolle</Label>
              <Select value={formData.role} onValueChange={v => setFormData({ ...formData, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="waiter">Kellner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(formData.role === 'admin' || formData.role === 'manager') && (
              <div>
                <Label>Passwort {!editingUser && '*'}</Label>
                <Input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder={editingUser ? 'Leer = unverändert' : 'Passwort eingeben'} />
              </div>
            )}
            <div>
              <Label>PIN (4-6 Ziffern){formData.role === 'waiter' ? ' *' : ''}</Label>
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-slate-400" />
                <Input value={formData.pin} onChange={e => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder={formData.role === 'waiter' ? '1234' : 'Optional für POS Login'} maxLength={6} className="font-mono text-2xl tracking-widest" />
              </div>
              <p className="mt-1 text-xs text-slate-400">
                {formData.role === 'waiter'
                  ? 'Für Kellner ist die PIN Pflicht.'
                  : 'Manager und Admin können zusätzlich eine PIN erhalten, damit sie sich direkt im POS anmelden und Kassenabschluss machen können.'}
              </p>
            </div>

            {/* Assigned roles */}
            {roles.length > 0 && (
              <div>
                <Label>Rollen zuweisen</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {roles.map(role => {
                    const assigned = formData.roles.includes(role.id);
                    return (
                      <button
                        key={role.id}
                        onClick={() => toggleRole(role.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          assigned ? 'text-white border-transparent' : 'text-slate-600 border-slate-200 bg-white hover:border-slate-400'
                        }`}
                        style={assigned ? { backgroundColor: role.color || '#1a2f38' } : {}}
                      >
                        {role.name}
                        {assigned && <X className="w-3 h-3" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked permissions (collapsible) */}
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setShowLockedSection(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-red-400" />
                  Gesperrte Berechtigungen
                  {formData.locked_permissions.length > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-xs">{formData.locked_permissions.length}</Badge>
                  )}
                </span>
                {showLockedSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showLockedSection && (
                <div className="p-3">
                  <p className="text-xs text-slate-400 mb-2">Gesperrte Berechtigungen überschreiben alle Rollen-Grants.</p>
                  <div className="grid grid-cols-2 gap-1">
                    {ALL_PERMISSIONS.map(perm => {
                      const locked = formData.locked_permissions.includes(perm);
                      return (
                        <label key={perm} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs ${locked ? 'bg-red-50 text-red-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                          <input type="checkbox" checked={locked} onChange={() => toggleLockedPerm(perm)} className="accent-red-500" />
                          {perm.replace(/_/g, ' ')}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Allowed registers (collapsible) */}
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setShowRegistersSection(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <span>
                  Erlaubte Kassen
                  {formData.allowed_registers.length > 0 && (
                    <span className="ml-2 text-xs text-slate-400">({formData.allowed_registers.join(', ')})</span>
                  )}
                </span>
                {showRegistersSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showRegistersSection && (
                <div className="p-3 space-y-2">
                  <p className="text-xs text-slate-400">Leer = alle Kassen erlaubt.</p>
                  <div className="flex flex-wrap gap-1">
                    {formData.allowed_registers.map(r => (
                      <span key={r} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded">
                        {r}
                        <button onClick={() => setFormData(f => ({ ...f, allowed_registers: f.allowed_registers.filter(x => x !== r) }))}>
                          <X className="w-3 h-3 hover:text-red-500" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newRegister} onChange={e => setNewRegister(e.target.value)} onKeyDown={e => e.key === 'Enter' && addRegister()} placeholder="z.B. POS_001" className="text-sm flex-1" />
                    <Button variant="outline" size="sm" onClick={addRegister}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </div>

            {/* Effective permissions preview (only when editing) */}
            {editingUser && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600">Effektive Berechtigungen (Vorschau)</span>
                  <Button variant="outline" size="sm" onClick={loadEffective} disabled={loadingEffective} className="h-6 text-xs px-2">
                    {loadingEffective ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Berechnen'}
                  </Button>
                </div>
                {effectivePerms === null ? (
                  <p className="text-xs text-slate-400">Klicke "Berechnen" um die effektiven Berechtigungen anzuzeigen.</p>
                ) : effectivePerms.length === 0 ? (
                  <p className="text-xs text-slate-400">Keine effektiven Berechtigungen.</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {effectivePerms.map(p => (
                      <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        formData.locked_permissions.includes(p) ? 'bg-red-100 text-red-600 line-through' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {p.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Button onClick={handleSave} className="w-full bg-[#1a2f38]" data-testid="save-user-btn">
              {editingUser ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
